import useProfileDrafts, { ProfileDraftShelf } from '../useProfileDrafts';
import ProfilePhotoGalleryEditor from '../components/ProfilePhotoGalleryEditor';
import ArtistTaxonomyFields from '@/components/ArtistTaxonomyFields';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import adminApi from '@/lib/adminApi';
import { resolveAdminCityLocation } from '@/lib/adminLocation';
import { artistProfilePath } from '@/lib/artistSlug';
import {
  artistCategoryOptions,
  getKnownArtCategories,
  getKnownArtSubcategories,
  getOtherArtSubcategories,
  getSubcategoryOptionsForCategories,
  joinArtValues,
  splitArtValues,
} from '@/lib/artistCategories';
import { analyzeArtistProfile, buildArtistBioSuggestions, profileScoreBadgeClass, profileScoreBarClass } from '@/lib/profileCoach';

const emptyArtistForm = {
  name: '',
  mobile: '',
  email: '',
  display_profession: '',
  seo_slug: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  image_alt_text: '',
  image_title: '',
  image_caption: '',
  og_title: '',
  og_description: '',
  category: '',
  category_other: '',
  subcategory: '',
  subcategory_other: '',
  city: '',
  state: '',
  country: 'India',
  instagram: '',
  instagram_followers: '',
  facebook: '',
  spotify: '',
  youtube: '',
  voice_intro_url: '',
  image_url: '',
  portfolio_image_urls: ['', '', ''],
  portfolio_video_urls: ['', ''],
  bio: '',
  native_bio: '',
  native_language: '',
  native_language_verified: false,
  native_language_badge: '',
  trust_badge_override: 'none',
  seasonal_availability: [],
  is_verified: false,
  is_featured: false,
  is_spotlight: false,
  login_password: '',
  send_login_email: true,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const ADMIN_ARTIST_FALLBACK_EMAILS = ['me@kalamanch.art', 'artist@kalamanch.art'];
const MAX_PORTFOLIO_IMAGES = 20;
const MAX_PORTFOLIO_VIDEOS = 2;

const ARTIST_SEO_FIELDS = [
  'seo_slug',
  'meta_title',
  'meta_description',
  'meta_keywords',
  'image_alt_text',
  'image_title',
  'image_caption',
  'og_title',
  'og_description',
];
const withFixedSlots = (values, count) => [...(Array.isArray(values) ? values : []), ...Array(count).fill('')].slice(0, count);
const isAdminFallbackArtistEmail = (email) =>
  ADMIN_ARTIST_FALLBACK_EMAILS.includes(String(email || '').trim().toLowerCase());

const isValidUrlForHosts = (value, hosts) => {
  if (!value) return true;
  try {
    const parsed = new URL(value);
    return hosts.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`));
  } catch (error) {
    return false;
  }
};

const normalizeMobileForMatch = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

const buildArtistAdminPayload = (formData) => ({
  ...formData,
  instagram_followers: formData.instagram_followers === '' ? null : Number(formData.instagram_followers),
  display_profession: String(formData.display_profession || '').trim(),
  category: joinArtValues([...splitArtValues(formData.category), ...splitArtValues(formData.category_other)]),
  subcategory: joinArtValues([...splitArtValues(formData.subcategory), ...splitArtValues(formData.subcategory_other)]),
  portfolio_image_urls: formData.portfolio_image_urls.map((value) => value.trim()).filter(Boolean),
  portfolio_video_urls: formData.portfolio_video_urls.map((value) => value.trim()).filter(Boolean),
  seasonal_availability: (formData.seasonal_availability || []).map((value) => value.trim()).filter(Boolean),
  login_password: String(formData.login_password || '').trim(),
  send_login_email: Boolean(formData.send_login_email),
});

const hasText = (value) => Boolean(String(value || '').trim());
const hasNativeReviewPending = (artist) =>
  hasText(artist.native_bio) && !artist.native_language_verified;
const hasNativeProfile = (artist) =>
  hasText(artist.native_bio) || hasText(artist.native_language) || hasText(artist.native_language_badge);
const hasPushOptIn = (artist) =>
  Boolean(artist.push_notifications_opted_in || Number(artist.push_notification_device_count || 0) > 0);
const hasVerifiedEmail = (account = {}) => account.email_verified === true || account.email_verified_at;
const isArtistReviewReady = (artist = {}) => hasVerifiedEmail(artist) && analyzeArtistProfile(artist).score >= 70;
const socialProfileConfig = [
  { key: 'instagram', label: 'Instagram', shortLabel: 'IG', tone: 'border-pink-200 bg-pink-50 text-pink-800' },
  { key: 'facebook', label: 'Facebook', shortLabel: 'FB', tone: 'border-blue-200 bg-blue-50 text-blue-800' },
  { key: 'youtube', label: 'YouTube', shortLabel: 'YT', tone: 'border-red-200 bg-red-50 text-red-800' },
  { key: 'spotify', label: 'Spotify', shortLabel: 'SP', tone: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
];
const TRUST_BADGE_OPTIONS = [
  { value: 'none', label: 'No public trust badge' },
  { value: 'verified_profile', label: 'Verified Profile' },
  { value: 'verified_artist', label: 'Verified Artist' },
  { value: 'performance_verified', label: 'Performance Verified' },
];
const trustBadgeLabel = (value) =>
  TRUST_BADGE_OPTIONS.find((option) => option.value === value)?.label || '';
const socialProfileDisplayName = (url = '') => {
  try {
    const parsed = new URL(String(url || '').trim());
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    return pathParts[0] ? `@${pathParts[0].replace(/^@/, '')}` : parsed.hostname.replace(/^www\./, '');
  } catch (error) {
    return String(url || '').trim();
  }
};
const formatFollowerCount = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return '';
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1)}M followers`;
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}K followers`;
  return `${count} follower${count === 1 ? '' : 's'}`;
};
const socialFollowerCount = (account, key) =>
  account[`${key}_followers`] ||
  account[`${key}_followers_count`] ||
  account[`${key}_follower_count`] ||
  account.social_followers?.[key] ||
  account.social_profile_followers?.[key] ||
  '';
const getArtistSocialProfiles = (artist) =>
  socialProfileConfig
    .map((profile) => ({
      ...profile,
      url: String(artist[profile.key] || '').trim(),
      followerLabel: formatFollowerCount(socialFollowerCount(artist, profile.key)),
    }))
    .filter((profile) => profile.url);
const isAdminCreatedArtist = (artist) =>
  artist.profile_source === 'admin' ||
  artist.created_by_admin ||
  artist.admin_created_login ||
  artist.uses_admin_fallback_email ||
  isAdminFallbackArtistEmail(artist.email);
const profileSourceLabel = (artist) =>
  isAdminCreatedArtist(artist) ? 'Created by Admin' : 'Created by Artist';
const profileSourceBadgeClass = (artist) =>
  isAdminCreatedArtist(artist)
    ? 'border-sky-200 bg-sky-50 text-sky-800'
    : 'border-emerald-200 bg-emerald-50 text-emerald-800';
const suggestedNativeBadge = (artist = {}) => {
  const language = String(artist.native_language || '').trim();
  const artForm = String(artist.subcategory || artist.category || 'Artist').trim();
  return language ? `Verified ${language} ${artForm}` : `Verified Native ${artForm}`;
};

const seasonalAvailabilityOptions = [
  'Holi',
  'Navratri / Garba',
  'Diwali',
  'Independence Day',
  'Republic Day',
  'Durga Puja',
  'Ganesh Utsav',
  'Janmashtami',
  'Eid / Mushaira',
  'Christmas',
  'Wedding / Sangeet',
  'College Fest',
  'Corporate Event',
  'School Annual Day',
  'Society Program',
  'Heritage / Folk Festival',
  'Open Mic',
  'Devotional Program',
];

const getArtistReviewChecklist = (artist) => [
  ...analyzeArtistProfile(artist).checklist,
  { label: 'Verified', complete: Boolean(artist.is_verified) },
];

const formatPushSync = (value) => {
  if (!value) return 'No active app device';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Device synced';
  return `Last synced ${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};
const formatCreatedAt = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const getDaysOnKalamanch = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diff = Date.now() - date.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};
const formatDateTime = (value) => {
  if (!value) return 'Not generated yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not generated yet';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};
const buildMilestoneMessage = (artist, milestoneLabel) => ({
  subject: `${milestoneLabel} on KalaManch`,
  message: `Namaste ${artist.name || 'Artist'},\n\nCongratulations on completing ${milestoneLabel} with KalaManch.art. We are glad to have you as part of the platform and we would love to see your profile keep growing with updated portfolio links, videos, and fresh work.\n\nWarm wishes,\nKalaManch Team`,
});

const adminArtistStatusLabels = {
  all: 'All artists',
  verified: 'Verified',
  unverified: 'Unverified',
  pending_email: 'Pending email verification',
  review_ready: 'Ready for review',
  incomplete_signup: 'Incomplete signup',
  featured: 'Featured',
  spotlight: 'Spotlight',
  native_pending: 'Native bio pending review',
  native_verified: 'Native language verified',
  native_added: 'Native language added',
  push_on: 'Push notifications opted-in',
  push_off: 'Push notifications off/no app device',
  standard: 'Not featured',
};

const ArtistActionButton = ({ children, className = '', ...props }) => (
  <button
    type="button"
    className={`rounded-full px-4 py-2 text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
    {...props}
  >
    {children}
  </button>
);

const resolveSubcategoryValue = (category, value) => {
  const categories = splitArtValues(category);
  return {
    subcategory: joinArtValues(getKnownArtSubcategories(categories, value)),
    subcategory_other: joinArtValues(getOtherArtSubcategories(categories, value)),
  };
};

const resolveArtistArtValues = (categoryValue, subcategoryValue) => {
  const knownCategories = getKnownArtCategories(categoryValue);
  const allowedSubcategories = new Set(getSubcategoryOptionsForCategories(knownCategories));
  const categoryTokens = splitArtValues(categoryValue);
  const subcategoriesFromCategory = categoryTokens.filter(
    (item) => !knownCategories.includes(item) && allowedSubcategories.has(item)
  );
  const customCategories = categoryTokens.filter(
    (item) => !knownCategories.includes(item) && !allowedSubcategories.has(item) && item !== 'Other'
  );
  const categoryValues = {
    category: joinArtValues(knownCategories),
    category_other: joinArtValues(customCategories),
  };
  const allSubcategoryValues = joinArtValues([...subcategoriesFromCategory, ...splitArtValues(subcategoryValue)]);
  const subcategoryValues = resolveSubcategoryValue(categoryValues.category, allSubcategoryValues);
  return {
    ...categoryValues,
    ...subcategoryValues,
  };
};

const getMainCategoryValues = (value) => {
  const categoryValues = resolveArtistArtValues(value, '');
  return [...splitArtValues(categoryValues.category), ...splitArtValues(categoryValues.category_other)];
};

const artistFormFromLead = (lead) => {
  const artValues = resolveArtistArtValues(lead.category || '', lead.subcategory || '');
  return {
    ...emptyArtistForm,
    name: lead.artist_name || '',
    mobile: lead.contact_mobile || '',
    email: lead.contact_email || '',
    category: artValues.category,
    category_other: artValues.category_other,
    subcategory: artValues.subcategory,
    subcategory_other: artValues.subcategory_other,
    city: lead.city || '',
    state: lead.state || '',
    country: lead.country || 'India',
      instagram: lead.instagram_url || (lead.instagram_handle ? `https://www.instagram.com/${lead.instagram_handle}/` : ''),
    bio: lead.notes ? `Draft from artist lead.\n\n${lead.notes}` : 'Draft from artist lead.',
    is_verified: false,
    is_featured: false,
    is_spotlight: false,
  };
};

const AdminArtists = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [busyArtistId, setBusyArtistId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingArtistId, setEditingArtistId] = useState('');
  const [draftLeadId, setDraftLeadId] = useState('');
  const [formData, setFormData] = useState(emptyArtistForm);
  const [formBusy, setFormBusy] = useState(false);
  const [seoBusy, setSeoBusy] = useState(false);
  const [aiSeoBusy, setAiSeoBusy] = useState(false);
  const [locationLookupBusy, setLocationLookupBusy] = useState(false);
  const [aiArtistBrief, setAiArtistBrief] = useState('');
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const draftStore = useProfileDrafts('artist', showForm, { formData, editingId: editingArtistId, brief: aiArtistBrief, leadId: draftLeadId });
  const restoreProfileDraft = (draft) => {
    setFormData({ ...emptyArtistForm, ...draft.formData, login_password: '' });
    setEditingArtistId(draft.editingId || '');
    setAiArtistBrief(draft.brief || '');
    setDraftLeadId(draft.leadId || '');
    setFormError('');
    setShowForm(true);
  };
  const [imageUploading, setImageUploading] = useState(false);
  const [messageArtistId, setMessageArtistId] = useState('');
  const [artistMessage, setArtistMessage] = useState('');
  const [messageNotice, setMessageNotice] = useState('');
  const [trustBadgeNotice, setTrustBadgeNotice] = useState('');
  const [trustBadgeError, setTrustBadgeError] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const [aiPanelByArtist, setAiPanelByArtist] = useState({});
  const [aiPanelLoadingByArtist, setAiPanelLoadingByArtist] = useState({});
  const [aiPanelErrorByArtist, setAiPanelErrorByArtist] = useState({});
  const [instagramSyncBusy, setInstagramSyncBusy] = useState(false);
  const [instagramSyncNotice, setInstagramSyncNotice] = useState('');
  const [instagramSyncError, setInstagramSyncError] = useState('');
  const lastAutoSeoBioRef = useRef('');

  useEffect(() => {
    if (!showForm) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showForm]);
  const focusArtistId = useMemo(() => new URLSearchParams(location.search).get('focus') || '', [location.search]);
  const focusedArtistRef = useRef('');
  const listPositionRef = useRef(null);
  const rememberListPosition = () => {
    const row = Array.from(document.querySelectorAll('[id^="admin-artist-"]'))
      .find((element) => element.getBoundingClientRect().bottom > 0);
    listPositionRef.current = {
      id: row?.id,
      top: row?.getBoundingClientRect().top,
      scrollY: window.scrollY,
    };
  };
  useLayoutEffect(() => {
    const position = listPositionRef.current;
    if (!position) return;
    listPositionRef.current = null;
    const row = position.id && document.getElementById(position.id);
    window.scrollTo({
      top: row ? window.scrollY + row.getBoundingClientRect().top - position.top : position.scrollY,
      behavior: 'instant',
    });
  }, [artists]);

  const loadArtists = async () => {
    const response = await adminApi.get('/admin/artists');
    const nextArtists = response.data.artists || [];
    rememberListPosition();
    setArtists(nextArtists);
    return nextArtists;
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const response = await adminApi.get('/admin/artists');
        if (mounted) {
          setArtists(response.data.artists || []);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const lead = location.state?.draftLead;
    if (!lead) return;

    setEditingArtistId('');
    setDraftLeadId(lead.id || '');
    setFormData(artistFormFromLead(lead));
    setFormError('');
    setFormNotice('Draft loaded from Artist Leads. Complete required fields, then save when ready.');
    setShowForm(true);
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.state, navigate]);

  const categories = useMemo(() => {
    return Array.from(new Set(artists.flatMap((artist) => getMainCategoryValues(artist.category)).filter(Boolean))).sort();
  }, [artists]);

  const allCategoryOptions = useMemo(() => {
    return Object.keys(artistCategoryOptions).filter((category) => category !== 'Other').sort();
  }, []);

  const selectedCategories = useMemo(() => splitArtValues(formData.category), [formData.category]);
  const hasSelectedMainCategory = Boolean(splitArtValues(formData.category)[0] || splitArtValues(formData.category_other)[0]);
  const selectedSubcategories = useMemo(() => splitArtValues(formData.subcategory), [formData.subcategory]);
  const subcategoryOptions = useMemo(() => {
    return getSubcategoryOptionsForCategories(selectedCategories);
  }, [selectedCategories]);
  const filteredCategoryOptions = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    return allCategoryOptions
      .filter((category) => category !== 'Other')
      .filter((category) => !query || category.toLowerCase().includes(query));
  }, [allCategoryOptions, categorySearch]);
  const filteredSubcategoryOptions = useMemo(() => {
    const query = subcategorySearch.trim().toLowerCase();
    return subcategoryOptions.filter((subcategory) => !query || subcategory.toLowerCase().includes(query));
  }, [subcategoryOptions, subcategorySearch]);

  const summary = useMemo(() => {
    const scoredArtists = artists.map((artist) => analyzeArtistProfile(artist).score);
    const averageScore = scoredArtists.length
      ? Math.round(scoredArtists.reduce((total, score) => total + score, 0) / scoredArtists.length)
      : 0;
    return {
      total: artists.length,
      verified: artists.filter((artist) => artist.is_verified).length,
      featured: artists.filter((artist) => artist.is_featured).length,
      spotlight: artists.filter((artist) => artist.is_spotlight).length,
      withPortfolio: artists.filter((artist) => artist.instagram || artist.spotify || artist.youtube).length,
      nativePending: artists.filter(hasNativeReviewPending).length,
      pushOptedIn: artists.filter(hasPushOptIn).length,
      pushActiveDevices: artists.reduce((total, artist) => total + Number(artist.push_notification_device_count || 0), 0),
      averageScore,
    };
  }, [artists]);

  const filteredArtists = useMemo(() => {
    const query = search.trim().toLowerCase();
    return artists.filter((artist) => {
      const matchesQuery =
        !query ||
        [
          artist.name,
          artist.email,
          artist.mobile,
          artist.category,
          artist.subcategory,
          artist.city,
          artist.native_bio,
          artist.native_language,
          artist.native_language_badge,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'verified' && artist.is_verified) ||
        (statusFilter === 'unverified' && !artist.is_verified) ||
        (statusFilter === 'pending_email' && !hasVerifiedEmail(artist)) ||
        (statusFilter === 'review_ready' && !artist.is_verified && isArtistReviewReady(artist)) ||
        (statusFilter === 'incomplete_signup' && !artist.is_verified && !isArtistReviewReady(artist)) ||
        (statusFilter === 'featured' && artist.is_featured) ||
        (statusFilter === 'spotlight' && artist.is_spotlight) ||
        (statusFilter === 'native_pending' && hasNativeReviewPending(artist)) ||
        (statusFilter === 'native_verified' && artist.native_language_verified) ||
        (statusFilter === 'native_added' && hasNativeProfile(artist)) ||
        (statusFilter === 'push_on' && hasPushOptIn(artist)) ||
        (statusFilter === 'push_off' && !hasPushOptIn(artist)) ||
        (statusFilter === 'standard' && !artist.is_featured);

      const matchesCategory = categoryFilter === 'all' || getMainCategoryValues(artist.category).includes(categoryFilter);

      return matchesQuery && matchesStatus && matchesCategory;
    });
  }, [artists, search, statusFilter, categoryFilter]);

  useEffect(() => {
    if (!focusArtistId || loading) return;
    setSearch('');
    setStatusFilter('all');
    setCategoryFilter('all');
  }, [focusArtistId, loading]);

  useEffect(() => {
    if (!focusArtistId) {
      focusedArtistRef.current = '';
      return undefined;
    }
    if (loading || focusedArtistRef.current === focusArtistId) return undefined;
    const timer = window.setTimeout(() => {
      const row = document.getElementById(`admin-artist-${focusArtistId}`);
      if (!row) return;
      focusedArtistRef.current = focusArtistId;
      row.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [focusArtistId, loading, filteredArtists]);

  const resetForm = ({ keepNotice = false } = {}) => {
    if (keepNotice) draftStore.complete();
    else if (showForm && !draftStore.save()) return;

    lastAutoSeoBioRef.current = '';
    setFormData(emptyArtistForm);
    setAiArtistBrief('');
    setEditingArtistId('');
    setDraftLeadId('');
    setShowForm(false);
    setFormError('');
    if (!keepNotice) setFormNotice('');
  };

  const saveCurrentDraft = () => {
    if (draftStore.save()) setFormNotice('Draft saved. Cancel or Close keeps it. Use Saved drafts → Continue draft to reopen.');
  };

  const removeDraftLeadIfNeeded = async (leadId) => {
    if (!leadId) return false;
    try {
      await adminApi.delete(`/admin/artist-leads/${leadId}`);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleFormChange = (field, value) => {
    setFormError('');
    setFormNotice('');
    setFormData((current) => {
      return { ...current, [field]: value };
    });
  };

  const toggleCategory = (category) => {
    setFormError('');
    setFormNotice('');
    setFormData((current) => {
      const currentCategories = splitArtValues(current.category);
      const nextCategories = currentCategories.includes(category)
        ? currentCategories.filter((item) => item !== category)
        : [...currentCategories, category];
      const allowedSubcategories = new Set(getSubcategoryOptionsForCategories(nextCategories));
      const nextSubcategories = splitArtValues(current.subcategory).filter((item) => allowedSubcategories.has(item));
      return {
        ...current,
        category: joinArtValues(nextCategories),
        subcategory: joinArtValues(nextSubcategories),
      };
    });
  };

  const toggleSubcategory = (subcategory) => {
    setFormError('');
    setFormNotice('');
    setFormData((current) => {
      const currentSubcategories = splitArtValues(current.subcategory);
      const nextSubcategories = currentSubcategories.includes(subcategory)
        ? currentSubcategories.filter((item) => item !== subcategory)
        : [...currentSubcategories, subcategory];
      return { ...current, subcategory: joinArtValues(nextSubcategories) };
    });
  };

  const toggleSeasonalAvailability = (option) => {
    setFormError('');
    setFormNotice('');
    setFormData((current) => {
      const selected = Array.isArray(current.seasonal_availability) ? current.seasonal_availability : [];
      return {
        ...current,
        seasonal_availability: selected.includes(option)
          ? selected.filter((item) => item !== option)
          : [...selected, option],
      };
    });
  };

  const handleSlotChange = (field, index, value) => {
    setFormData((current) => {
      const limit = field === 'portfolio_image_urls' ? MAX_PORTFOLIO_IMAGES : MAX_PORTFOLIO_VIDEOS;
      const nextValues = [...withFixedSlots(current[field], limit)];
      nextValues[index] = value;
      return { ...current, [field]: nextValues };
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const uploadPayload = new FormData();
    uploadPayload.append('file', file);
    setImageUploading(true);
    setFormError('');
    setFormNotice('');

    try {
      const response = await adminApi.post('/uploads/profile-image', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((current) => ({ ...current, image_url: response.data.image_url || '' }));
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Unable to upload profile image.');
    } finally {
      setImageUploading(false);
      event.target.value = '';
    }
  };

  const handlePortfolioImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const uploadPayload = new FormData();
    uploadPayload.append('file', file);
    setImageUploading(true);
    setFormError('');
    setFormNotice('');

    try {
      const response = await adminApi.post('/uploads/profile-image', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleSlotChange('portfolio_image_urls', index, response.data.image_url || '');
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Unable to upload portfolio photo.');
    } finally {
      setImageUploading(false);
      event.target.value = '';
    }
  };

  const handleEdit = (artist, notice = '') => {
    draftStore.start();
    const artValues = resolveArtistArtValues(artist.category || '', artist.subcategory || '');
    setEditingArtistId(artist.id);
    lastAutoSeoBioRef.current = ARTIST_SEO_FIELDS.some((field) => hasText(artist[field])) ? artist.bio || '' : '';
    setAiArtistBrief('');
    setDraftLeadId('');
    setFormData({
      name: artist.name || '',
      mobile: artist.mobile || '',
      email: artist.email || '',
      display_profession: artist.display_profession || '',
      seo_slug: artist.seo_slug || artist.slug || '',
      meta_title: artist.meta_title || '',
      meta_description: artist.meta_description || '',
      meta_keywords: artist.meta_keywords || '',
      image_alt_text: artist.image_alt_text || '',
      image_title: artist.image_title || '',
      image_caption: artist.image_caption || '',
      og_title: artist.og_title || '',
      og_description: artist.og_description || '',
      category: artValues.category,
      category_other: artValues.category_other,
      subcategory: artValues.subcategory,
      subcategory_other: artValues.subcategory_other,
      city: artist.city || '',
      state: artist.state || '',
      country: artist.country || 'India',
      instagram: artist.instagram || '',
      instagram_followers: artist.instagram_followers ?? '',
      facebook: artist.facebook || '',
      spotify: artist.spotify || '',
      youtube: artist.youtube || '',
      voice_intro_url: artist.voice_intro_url || '',
      image_url: artist.image_url || '',
      portfolio_image_urls: withFixedSlots(artist.portfolio_image_urls, MAX_PORTFOLIO_IMAGES),
      portfolio_video_urls: withFixedSlots(artist.portfolio_video_urls, MAX_PORTFOLIO_VIDEOS),
      bio: artist.bio || '',
      native_bio: artist.native_bio || '',
      native_language: artist.native_language || '',
      native_language_verified: Boolean(artist.native_language_verified),
      native_language_badge: artist.native_language_badge || '',
      trust_badge_override: artist.trust_badge_override || 'none',
      seasonal_availability: Array.isArray(artist.seasonal_availability) ? artist.seasonal_availability : [],
      is_verified: Boolean(artist.is_verified),
      is_featured: Boolean(artist.is_featured),
      is_spotlight: Boolean(artist.is_spotlight),
      login_password: '',
      send_login_email: true,
    });
    setFormError('');
    setFormNotice(notice);
    setShowForm(true);
  };

  const handleCreate = () => {
    draftStore.start();
    setEditingArtistId('');
    setDraftLeadId('');
    lastAutoSeoBioRef.current = '';
    setAiArtistBrief('');
    setFormData(emptyArtistForm);
    setFormError('');
    setFormNotice('');
    setShowForm(true);
  };

  const handleCityBlur = async () => {
    const requestedCity = formData.city.trim();
    const stateBeforeLookup = formData.state.trim();
    const countryBeforeLookup = formData.country.trim();
    if (!requestedCity) return;
    setLocationLookupBusy(true);
    try {
      const locationResult = await resolveAdminCityLocation(requestedCity, formData.country);
      if (!locationResult) return;
      setFormData((current) => {
        if (current.city.trim().toLowerCase() !== requestedCity.toLowerCase()) return current;
        return {
          ...current,
          state: current.state.trim() === stateBeforeLookup ? locationResult.state || current.state : current.state,
          country: current.country.trim() === countryBeforeLookup ? locationResult.country || current.country || 'India' : current.country,
        };
      });
    } catch (error) {
      // Keep State and Country editable when automatic lookup is unavailable.
    } finally {
      setLocationLookupBusy(false);
    }
  };

  const generateArtistBioSeo = async ({
    generateBio = false,
    bioOverride = '',
    automatic = false,
    overwriteSeo = false,
  } = {}) => {
    const sourceBio = String(bioOverride || formData.bio || '').trim();
    const category = joinArtValues([...splitArtValues(formData.category), ...splitArtValues(formData.category_other)]);
    const subcategory = joinArtValues([...splitArtValues(formData.subcategory), ...splitArtValues(formData.subcategory_other)]);
    if (!formData.name.trim() || !category || !formData.city.trim()) {
      if (!automatic) setFormError('Add the artist name, category, and city before using ChatGPT.');
      return;
    }
    if (generateBio && (!formData.state.trim() || !formData.country.trim())) {
      if (!automatic) setFormError('Add the artist city, state, and country before generating the Bio.');
      return;
    }
    if (sourceBio.length < 40) {
      if (!automatic) {
        setFormError(generateBio ? 'Add fuller artist details in the ChatGPT box first.' : 'Add a fuller artist bio before using ChatGPT for SEO.');
      }
      return;
    }
    if (automatic && (ARTIST_SEO_FIELDS.some((field) => hasText(formData[field])) || lastAutoSeoBioRef.current === sourceBio)) {
      return;
    }

    setAiSeoBusy(true);
    setFormError('');
    setFormNotice(automatic ? 'Creating SEO fields from the artist bio...' : 'ChatGPT is creating the artist bio and SEO fields...');
    try {
      const response = await adminApi.post('/admin/artists/ai-seo-draft', {
        name: formData.name,
        category,
        subcategory,
        display_profession: formData.display_profession,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        bio: sourceBio,
        instagram: formData.instagram,
        generate_bio: generateBio,
      });
      const generated = response.data?.seo || {};
      setFormData((current) => {
        const next = { ...current, bio: generateBio ? generated.bio || sourceBio : sourceBio };
        ARTIST_SEO_FIELDS.forEach((field) => {
          if (overwriteSeo || !hasText(current[field])) next[field] = generated[field] || current[field] || '';
        });
        return next;
      });
      lastAutoSeoBioRef.current = generateBio ? generated.bio || sourceBio : sourceBio;
      setFormNotice(
        generateBio
          ? 'ChatGPT created the bio and filled URL, metadata, image SEO, and Open Graph fields. Review them, then save the artist.'
          : 'SEO fields were filled automatically from the bio. Review them, then save the artist.'
      );
    } catch (error) {
      setFormError(error.response?.data?.detail || 'ChatGPT could not create the artist SEO fields right now.');
      setFormNotice('');
    } finally {
      setAiSeoBusy(false);
    }
  };

  const handleBioBlur = () => {
    generateArtistBioSeo({ automatic: true });
  };

  const handleInstagramFollowerSync = async () => {
    setInstagramSyncBusy(true);
    setInstagramSyncNotice('');
    setInstagramSyncError('');
    try {
      const response = await adminApi.post('/admin/instagram-followers/sync?limit=25');
      const result = response.data || {};
      await loadArtists();
      setInstagramSyncNotice(
        `Instagram sync complete: ${result.artists_updated || 0} artist(s) updated, ${result.profiles_failed || 0} profile(s) unavailable.`
      );
    } catch (error) {
      setInstagramSyncError(error.response?.data?.detail || 'Instagram follower sync could not run.');
    } finally {
      setInstagramSyncBusy(false);
    }
  };

  const findMatchingCreatedArtist = (artistRows) => {
    const normalizedFormMobile = normalizeMobileForMatch(formData.mobile);
    const normalizedFormEmail = isAdminFallbackArtistEmail(formData.email) ? '' : formData.email.trim().toLowerCase();
    const normalizedFormName = formData.name.trim().toLowerCase();
    const normalizedFormCity = formData.city.trim().toLowerCase();

    return artistRows.find((artist) => {
      const matchesMobile =
        normalizedFormMobile &&
        normalizeMobileForMatch(artist.mobile) === normalizedFormMobile;
      const matchesEmail =
        normalizedFormEmail &&
        String(artist.email || '').trim().toLowerCase() === normalizedFormEmail;
      const matchesFallbackIdentity =
        isAdminFallbackArtistEmail(formData.email) &&
        normalizedFormName &&
        normalizedFormCity &&
        String(artist.name || '').trim().toLowerCase() === normalizedFormName &&
        String(artist.city || '').trim().toLowerCase() === normalizedFormCity;
      return matchesMobile || matchesEmail || matchesFallbackIdentity;
    });
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormBusy(true);
    setFormError('');
    setFormNotice('');

    try {
      const sourceLeadId = draftLeadId;
      const payload = buildArtistAdminPayload(formData);

      if (!payload.category) {
        throw new Error('Please choose a category.');
      }
      if (!payload.city.trim()) {
        throw new Error('City is required.');
      }
      if (!payload.bio.trim()) {
        throw new Error('Bio is required.');
      }
      if (String(payload.mobile || '').includes(',')) {
        throw new Error('Please enter only one mobile number in the mobile field.');
      }
      if (!emailPattern.test(payload.email.trim())) {
        throw new Error('Please enter a valid email address.');
      }
      const normalizedEmail = payload.email.trim().toLowerCase();
      if (normalizedEmail.endsWith('@kalamanch.art') && !isAdminFallbackArtistEmail(normalizedEmail)) {
        throw new Error('Use only me@kalamanch.art or artist@kalamanch.art as fallback artist emails.');
      }
      if (!isValidUrlForHosts(payload.instagram, ['instagram.com', 'www.instagram.com'])) {
        throw new Error('Please enter a valid Instagram profile URL.');
      }
      if (payload.instagram_followers !== null && (!Number.isInteger(payload.instagram_followers) || payload.instagram_followers < 0)) {
        throw new Error('Instagram followers must be a whole number of zero or more.');
      }
      if (payload.instagram_followers !== null && !payload.instagram) {
        throw new Error('Please add the Instagram profile URL before adding its follower count.');
      }
      if (!isValidUrlForHosts(payload.facebook, ['facebook.com', 'www.facebook.com', 'fb.com', 'm.facebook.com'])) {
        throw new Error('Please enter a valid Facebook page URL.');
      }
      if (!isValidUrlForHosts(payload.spotify, ['spotify.com', 'open.spotify.com'])) {
        throw new Error('Please enter a valid Spotify artist or profile URL.');
      }
      if (!isValidUrlForHosts(payload.youtube, ['youtube.com', 'www.youtube.com', 'youtu.be'])) {
        throw new Error('Please enter a valid YouTube profile or channel URL.');
      }
      if (payload.login_password && isAdminFallbackArtistEmail(payload.email)) {
        throw new Error('Use the artist real email before creating login credentials.');
      }

      if (editingArtistId) {
        await adminApi.put(`/admin/artists/${editingArtistId}`, payload);
        resetForm({ keepNotice: true });
        setFormNotice('Artist profile updated successfully.');
      } else {
        const response = await adminApi.post('/admin/artists', payload);
        const createdArtist = response.data?.artist;
        const leadRemoved = await removeDraftLeadIfNeeded(sourceLeadId);
        setSearch('');
        setStatusFilter('all');
        setCategoryFilter('all');
        resetForm({ keepNotice: true });
        setFormNotice(
          leadRemoved
            ? 'Artist created successfully. The original artist lead was removed.'
            : `Artist created successfully.${createdArtist?.is_verified ? ' It is visible publicly.' : ' Verify the artist when ready to make the profile public.'}`
        );
        if (createdArtist?.id) {
          navigate(`${location.pathname}?focus=${encodeURIComponent(createdArtist.id)}`, { replace: true });
        }
      }
      await loadArtists();
    } catch (error) {
      if (!editingArtistId && !error.response) {
        try {
          const artistRows = await loadArtists();
          const matchingArtist = findMatchingCreatedArtist(artistRows);
          if (matchingArtist) {
            const leadRemoved = await removeDraftLeadIfNeeded(draftLeadId);
            setSearch('');
            setStatusFilter('all');
            setCategoryFilter('all');
            resetForm({ keepNotice: true });
            setFormNotice(
              leadRemoved
                ? 'Artist was created successfully. The original artist lead was removed.'
                : 'Artist was created successfully. The first request completed, but the response did not return cleanly.'
            );
            navigate(`${location.pathname}?focus=${encodeURIComponent(matchingArtist.id)}`, { replace: true });
            return;
          }
        } catch (lookupError) {
          // ignore lookup failure and fall through
        }
      }

      if (!editingArtistId && error.response?.status === 400 && /already exists/i.test(error.response?.data?.detail || '')) {
        try {
          const artistRows = await loadArtists();
          const matchingArtist = findMatchingCreatedArtist(artistRows);
          if (matchingArtist) {
            const leadRemoved = await removeDraftLeadIfNeeded(draftLeadId);
            setSearch('');
            setStatusFilter('all');
            setCategoryFilter('all');
            resetForm({ keepNotice: true });
            setFormNotice(
              leadRemoved
                ? 'This email already belongs to an existing artist account. I opened that profile and removed the duplicate artist lead. Complete the existing profile and resend verification; do not create another account.'
                : 'This email already belongs to an existing artist account. I opened that profile below. Use Edit Artist to complete it and Resend Verification Email if needed; do not create another account.'
            );
            navigate(`${location.pathname}?focus=${encodeURIComponent(matchingArtist.id)}`, { replace: true });
            return;
          }
        } catch (lookupError) {
          // ignore lookup failure and show the original backend message
        }
      }

      if (!error.response) {
        setFormError(error.message || 'Unable to save artist profile.');
      } else if (error.response?.status === 404) {
        setFormError('Artist admin save route is not live yet on the backend. Redeploy Render after pushing backend changes.');
      } else {
        setFormError(error.response?.data?.detail || 'Unable to save artist profile.');
      }
    } finally {
      setFormBusy(false);
    }
  };

  const handleSeoSave = async () => {
    if (!editingArtistId) {
      setFormNotice('Create the artist first, then reopen the profile to save custom SEO fields.');
      return;
    }
    setSeoBusy(true);
    setFormError('');
    setFormNotice('');
    try {
      await adminApi.patch(`/admin/artists/${editingArtistId}/seo`, {
        seo_slug: formData.seo_slug || '',
        meta_title: formData.meta_title || '',
        meta_description: formData.meta_description || '',
        meta_keywords: formData.meta_keywords || '',
        image_alt_text: formData.image_alt_text || '',
        image_title: formData.image_title || '',
        image_caption: formData.image_caption || '',
        og_title: formData.og_title || '',
        og_description: formData.og_description || '',
      });
      await loadArtists();
      setFormNotice('Dedicated SEO route live. Artist SEO fields saved. Public page SEO will now use these values instead of the auto-generated fallback.');
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          await adminApi.put(`/admin/artists/${editingArtistId}`, buildArtistAdminPayload(formData));
          await loadArtists();
          setFormNotice('Saved through fallback route. The dedicated SEO route is not live on the backend yet, but your SEO values were saved through the main artist update route.');
          return;
        } catch (fallbackError) {
          setFormError(fallbackError.response?.data?.detail || 'Unable to save artist SEO fields right now.');
          return;
        }
      }
      setFormError(error.response?.data?.detail || 'Unable to save artist SEO fields right now.');
    } finally {
      setSeoBusy(false);
    }
  };

  const handleVerify = async (artistId) => {
    setBusyArtistId(artistId);
    setTrustBadgeError('');
    setTrustBadgeNotice('');
    try {
      await adminApi.patch(`/admin/artists/${artistId}/verify`);
      await loadArtists();
      setTrustBadgeNotice('Artist profile verified.');
    } catch (error) {
      setTrustBadgeError(error.response?.data?.detail || 'Artist could not be verified.');
    } finally {
      setBusyArtistId('');
    }
  };

  const handleResendEmailVerification = async (artist) => {
    setBusyArtistId(artist.id);
    setTrustBadgeError('');
    setTrustBadgeNotice('');
    try {
      const response = await adminApi.post(`/admin/artists/${artist.id}/resend-email-verification`);
      setTrustBadgeNotice(response.data?.message || `Verification email sent to ${artist.email}.`);
      await loadArtists();
    } catch (error) {
      setTrustBadgeError(error.response?.data?.detail || 'Verification email could not be sent.');
    } finally {
      setBusyArtistId('');
    }
  };

  const handleFeature = async (artistId) => {
    setBusyArtistId(artistId);
    try {
      await adminApi.patch(`/admin/artists/${artistId}/feature`);
      await loadArtists();
    } finally {
      setBusyArtistId('');
    }
  };

  const handleUnfeature = async (artistId) => {
    setBusyArtistId(artistId);
    try {
      await adminApi.patch(`/admin/artists/${artistId}/unfeature`);
      await loadArtists();
    } finally {
      setBusyArtistId('');
    }
  };

  const handleSpotlight = async (artistId, isSpotlight) => {
    setBusyArtistId(artistId);
    try {
      await adminApi.patch(`/admin/artists/${artistId}/${isSpotlight ? 'unspotlight' : 'spotlight'}`);
      await loadArtists();
    } finally {
      setBusyArtistId('');
    }
  };

  const handleTrustBadgeOverride = async (artistId, value) => {
    const previousArtists = artists;
    rememberListPosition();
    setTrustBadgeNotice('');
    setTrustBadgeError('');
    setBusyArtistId(artistId);
    setArtists((current) =>
      current.map((artist) =>
        artist.id === artistId ? { ...artist, trust_badge_override: value } : artist
      )
    );
    try {
      await adminApi.patch(`/admin/artists/${artistId}/trust-badge`, {
        trust_badge_override: value,
      });
      setTrustBadgeNotice(`Trust badge saved: ${trustBadgeLabel(value) || 'No public trust badge'}.`);
      await loadArtists();
    } catch (error) {
      rememberListPosition();
      setArtists(previousArtists);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 404) {
        setTrustBadgeError('Trust badge save route is not live on the backend yet. Push and redeploy the backend, then try again.');
      } else {
        setTrustBadgeError(detail || 'Could not save the trust badge. Please refresh and try again.');
      }
    } finally {
      setBusyArtistId('');
    }
  };

  const loadArtistAiPanel = async (artistId, { force = false } = {}) => {
    if (!artistId) return;
    if (aiPanelByArtist[artistId] && !force) return;
    setAiPanelLoadingByArtist((current) => ({ ...current, [artistId]: true }));
    setAiPanelErrorByArtist((current) => ({ ...current, [artistId]: '' }));
    try {
      const response = await adminApi.get(`/admin/artists/${artistId}/ai-panel`);
      setAiPanelByArtist((current) => ({ ...current, [artistId]: response.data || {} }));
    } catch (error) {
      setAiPanelErrorByArtist((current) => ({
        ...current,
        [artistId]: error.response?.data?.detail || 'Could not load the AI artist panel right now.',
      }));
    } finally {
      setAiPanelLoadingByArtist((current) => ({ ...current, [artistId]: false }));
    }
  };

  const handleDelete = async (artistId) => {
    const shouldDelete = window.confirm('Delete this artist and all linked videos and PMB threads? This cannot be undone.');
    if (!shouldDelete) return;

    setBusyArtistId(artistId);
    try {
      await adminApi.delete(`/admin/artists/${artistId}`);
      await loadArtists();
      if (editingArtistId === artistId) {
        resetForm();
      }
    } finally {
      setBusyArtistId('');
    }
  };

  const handleSendAdminMessage = async (artistId) => {
    if (!artistMessage.trim()) return;
    setBusyArtistId(artistId);
    setMessageNotice('');
    try {
      await adminApi.post(`/admin/artists/${artistId}/messages`, {
        subject: 'Profile review note',
        message: artistMessage,
      });
      setArtistMessage('');
      setMessageArtistId('');
      setMessageNotice('Message sent to artist inbox.');
    } finally {
      setBusyArtistId('');
    }
  };

  return (
    <div className="space-y-6">
      <ProfileDraftShelf store={draftStore} onRestore={restoreProfileDraft} open={showForm} />
      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Artist management</h2>
            <p className="text-gray-600 mt-3">
              Create, edit, verify, feature, and clean up full artist profiles from one admin workspace.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="rounded-full border border-pink-200 bg-pink-50 px-5 py-3 text-sm font-bold text-pink-700 transition hover:bg-pink-100 disabled:cursor-wait disabled:opacity-60"
              onClick={handleInstagramFollowerSync}
              disabled={instagramSyncBusy}
            >
              {instagramSyncBusy ? 'Syncing Instagram…' : 'Test Instagram sync'}
            </button>
            <button type="button" className="btn-primary" onClick={handleCreate}>
              Add New Artist
            </button>
          </div>
        </div>
      </section>

      {instagramSyncNotice && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
          {instagramSyncNotice}
        </div>
      )}
      {instagramSyncError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {instagramSyncError}
        </div>
      )}

      {!showForm && formNotice && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
          {formNotice}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-950/45 px-3 py-4 sm:px-6 sm:py-8">
          <button
            type="button"
            aria-label="Close artist form"
            className="absolute inset-0 cursor-default"
            onClick={() => {
              if (!formBusy && !imageUploading) resetForm();
            }}
          />
          <section className="relative w-full max-w-6xl rounded-[2rem] border border-orange-100 bg-white p-5 shadow-2xl sm:p-6">
          <div className="sticky -top-5 z-10 -mx-5 -mt-5 mb-5 flex items-center justify-between gap-4 rounded-t-[2rem] border-b border-orange-100 bg-white/95 p-5 backdrop-blur sm:-mx-6 sm:-mt-6 sm:p-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{editingArtistId ? 'Edit artist profile' : 'Add artist from admin'}</h3>
              <p className="text-sm text-gray-500 mt-1">
                Save or cancel to return to the same place in the artist list.
              </p>
            </div>
            <button type="button" className="btn-outline" onClick={resetForm} disabled={formBusy || imageUploading}>Cancel</button>
          </div>

          <form className="space-y-5" onSubmit={handleFormSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <input value={formData.name} onChange={(event) => handleFormChange('name', event.target.value)} placeholder="Artist name" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
              <input value={formData.mobile} onChange={(event) => handleFormChange('mobile', event.target.value)} placeholder="Mobile (optional)" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
              <div className="space-y-2">
                <input
                  value={formData.email}
                  onChange={(event) => handleFormChange('email', event.target.value)}
                  placeholder="Artist email or KalaManch fallback email"
                  className="w-full rounded-2xl border border-gray-300 px-4 py-3"
                  required
                />
                <div className="flex flex-wrap gap-2 text-xs">
                  {ADMIN_ARTIST_FALLBACK_EMAILS.map((email) => (
                    <button
                      key={email}
                      type="button"
                      onClick={() => handleFormChange('email', email)}
                      className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 font-semibold text-orange-700"
                    >
                      Use {email}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">Use a fallback email only when the artist has not shared a personal email.</p>
              </div>
              <ArtistTaxonomyFields value={formData} onChange={setFormData} />
              <div className="md:col-span-2 xl:col-span-3 rounded-3xl border border-sky-100 bg-sky-50/60 p-4">
                <p className="text-sm font-bold text-sky-950">Main profession label for public cards</p>
                <p className="mt-1 text-xs text-sky-900">Admin-only field. This short label appears above the artist photo on public listing cards instead of combined tags like Singer +1.</p>
                <input
                  value={formData.display_profession}
                  onChange={(event) => handleFormChange('display_profession', event.target.value)}
                  placeholder="Example: Singer, Kathak Dancer, VJ, Wedding Photographer"
                  className="mt-3 w-full rounded-2xl border border-sky-200 bg-white px-4 py-3"
                />
              </div>
              <input value={formData.city} onChange={(event) => handleFormChange('city', event.target.value)} onBlur={handleCityBlur} placeholder="City" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
              <input value={formData.state} onChange={(event) => handleFormChange('state', event.target.value)} placeholder="State" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
              <input value={formData.country} onChange={(event) => handleFormChange('country', event.target.value)} placeholder="Country" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
              <p className="text-xs text-gray-500 md:col-span-2 xl:col-span-3 -mt-2">
                {locationLookupBusy ? 'Finding State and Country...' : 'State and Country fill automatically after you leave the City box. You can still edit them.'}
              </p>
              <input value={formData.instagram} onChange={(event) => handleFormChange('instagram', event.target.value)} placeholder="Instagram profile URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
              <label className="block">
                <span className="sr-only">Instagram follower count</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={formData.instagram_followers}
                  onChange={(event) => handleFormChange('instagram_followers', event.target.value)}
                  placeholder="Instagram followers (admin verified)"
                  className="w-full rounded-2xl border border-gray-300 px-4 py-3"
                />
              </label>
              <input value={formData.facebook} onChange={(event) => handleFormChange('facebook', event.target.value)} placeholder="Facebook page URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
              <input value={formData.spotify} onChange={(event) => handleFormChange('spotify', event.target.value)} placeholder="Spotify artist/profile URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
              <input value={formData.youtube} onChange={(event) => handleFormChange('youtube', event.target.value)} placeholder="YouTube profile or channel URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
              <input value={formData.voice_intro_url} onChange={(event) => handleFormChange('voice_intro_url', event.target.value)} placeholder="Voice intro audio URL (10-sec MP3/M4A/WAV)" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
              <p className="md:col-span-2 xl:col-span-3 text-xs text-gray-500 -mt-2">Optional for admin-created profiles. Add links when available to help verification. A follower count supplied by the Instagram sync appears publicly independently of KalaManch trust badges.</p>
              <div className="w-full md:col-span-2 xl:col-span-1 space-y-3">
                <input value={formData.image_url} onChange={(event) => handleFormChange('image_url', event.target.value)} placeholder="Profile image URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
                <label className="block">
                  <span className="sr-only">Upload profile image</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="block w-full text-sm text-gray-600 file:mr-4 file:rounded-full file:border-0 file:bg-orange-50 file:px-4 file:py-2 file:font-semibold file:text-[#FF6F00] hover:file:bg-orange-100" />
                </label>
              <p className="text-xs text-gray-500">{imageUploading ? 'Uploading image...' : 'Choose image from PC or mobile to upload directly.'}</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ProfilePhotoGalleryEditor value={formData.portfolio_image_urls} onChange={photos => handleFormChange('portfolio_image_urls', photos)} onBusyChange={setImageUploading} />
            <div className="rounded-3xl border border-purple-100 bg-purple-50/70 p-4">
              <p className="text-sm font-bold text-gray-900">Portfolio videos</p>
              <p className="mt-1 text-xs text-gray-500">Maximum {MAX_PORTFOLIO_VIDEOS}. Add public YouTube, Instagram, Facebook, Vimeo, or similar video URLs.</p>
              <div className="mt-4 space-y-3">
                {formData.portfolio_video_urls.map((url, index) => (
                  <input
                    key={`admin-portfolio-video-${index}`}
                    value={url}
                    onChange={(event) => handleSlotChange('portfolio_video_urls', index, event.target.value)}
                    placeholder={`Public video ${index + 1} URL`}
                    className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3"
                  />
                ))}
              </div>
            </div>
          </div>

          <textarea
            value={formData.bio}
            onChange={(event) => handleFormChange('bio', event.target.value)}
            onBlur={handleBioBlur}
            rows="5"
            placeholder="Artist bio"
            className="w-full rounded-2xl border border-gray-300 px-4 py-3"
            required
          />

          <div className="grid gap-4 rounded-3xl border border-emerald-100 bg-emerald-50/70 p-4 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-bold text-emerald-900">Native language</span>
              <input
                value={formData.native_language || ''}
                onChange={(event) => handleFormChange('native_language', event.target.value)}
                placeholder="Hindi, Urdu, Punjabi, Bhojpuri..."
                className="mt-2 w-full rounded-2xl border border-emerald-200 px-4 py-3"
              />
            </label>
            <label className="block">
              <span className="flex items-center justify-between gap-3 text-sm font-bold text-emerald-900">
                Native verification badge
                <button
                  type="button"
                  onClick={() => {
                    handleFormChange('native_language_badge', suggestedNativeBadge(formData));
                  }}
                  className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-extrabold text-emerald-800 hover:bg-emerald-200"
                >
                  Suggest
                </button>
              </span>
              <input
                value={formData.native_language_badge || ''}
                onChange={(event) => handleFormChange('native_language_badge', event.target.value)}
                placeholder="Featured Hindi Poet, Verified Punjabi Singer..."
                className="mt-2 w-full rounded-2xl border border-emerald-200 px-4 py-3"
              />
            </label>
            <textarea
              value={formData.native_bio || ''}
              onChange={(event) => handleFormChange('native_bio', event.target.value)}
              rows="5"
              placeholder="Native language bio"
              className="w-full rounded-2xl border border-emerald-200 px-4 py-3 md:col-span-2"
              dir="auto"
            />
            <label className="inline-flex items-center gap-3 text-sm font-bold text-emerald-900">
              <input
                type="checkbox"
                checked={Boolean(formData.native_language_verified)}
                onChange={(event) => handleFormChange('native_language_verified', event.target.checked)}
              />
              Approve native language verification badge
            </label>
            <p className="text-xs leading-5 text-emerald-900">
              Use only after reviewing the native-language bio for meaning, script, and respectful wording. The public badge appears only when this is checked. Suggested format: {suggestedNativeBadge(formData)}.
            </p>
          </div>

          <div className="rounded-3xl border border-amber-100 bg-amber-50/70 p-4">
            <p className="text-sm font-bold text-amber-900">Seasonal discovery availability</p>
            <p className="mt-1 text-xs text-gray-600">Admin can tag occasions this artist can perform for. These tags improve artist search during festival, wedding, school, society, and corporate discovery.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {seasonalAvailabilityOptions.map((option) => {
                const selected = (formData.seasonal_availability || []).includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleSeasonalAvailability(option)}
                    className={`rounded-full border px-3 py-2 text-xs font-bold transition ${
                      selected
                        ? 'border-[#FF6F00] bg-[#FF6F00] text-white shadow-sm'
                        : 'border-amber-200 bg-white text-amber-900 hover:border-[#FF6F00]'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-orange-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-bold text-purple-900">AI Bio + SEO Helper</p>
                <p className="mt-1 text-xs leading-5 text-gray-600">ChatGPT creates a detailed formatted bio with specialization and highlight bullet points, then fills the SEO URL, search metadata, image SEO, and Open Graph fields. Nothing is saved until you save the artist.</p>
              </div>
              <button
                type="button"
                onClick={() => generateArtistBioSeo({ generateBio: true, bioOverride: aiArtistBrief, overwriteSeo: true })}
                disabled={aiSeoBusy || formBusy}
                className="w-fit rounded-full bg-purple-700 px-4 py-2 text-xs font-extrabold text-white transition hover:bg-purple-800 disabled:cursor-wait disabled:opacity-60"
              >
                {aiSeoBusy ? 'Creating...' : 'Create Bio + SEO with ChatGPT'}
              </button>
            </div>
            <div className="mt-4 rounded-2xl border border-purple-100 bg-white/90 p-3">
              <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-purple-800" htmlFor="admin-artist-ai-brief">
                Artist details for ChatGPT
              </label>
              <textarea
                id="admin-artist-ai-brief"
                value={aiArtistBrief}
                onChange={(event) => setAiArtistBrief(event.target.value)}
                rows="7"
                placeholder="Paste rough artist details here: profession, location, availability, experience, event types, specializations, achievements, performance style, languages, and any facts that must appear. Short notes are fine. ChatGPT will create the finished Bio and SEO fields."
                className="mt-2 w-full rounded-2xl border border-purple-200 bg-white px-4 py-3 text-sm leading-6 text-gray-800 outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
              />
              <p className="mt-2 text-xs leading-5 text-gray-600">Only include confirmed facts. ChatGPT will not add awards, clients, locations, follower counts, or availability unless you provide them.</p>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              {buildArtistBioSuggestions(formData).map((suggestion) => (
                <div key={suggestion.label} className="rounded-2xl bg-white/85 p-3 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{suggestion.label}</p>
                    <button
                      type="button"
                      onClick={() => generateArtistBioSeo({ bioOverride: suggestion.text })}
                      className="rounded-full border border-purple-200 px-3 py-1 text-xs font-bold text-purple-700 hover:bg-purple-50"
                    >
                      Use + fill SEO
                    </button>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-gray-600">{suggestion.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-bold text-slate-950">Public page SEO</p>
                <p className="mt-1 text-xs leading-5 text-slate-600">
                  These fields control the artist page title, meta description, image metadata, and social sharing cards. Leave them blank to use KalaManch's automatic SEO fallback.
                </p>
              </div>
              <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700">
                Auto-filled from Bio
              </span>
            </div>
            <div className="mt-4 grid gap-3">
              <input
                value={formData.seo_slug || ''}
                onChange={(event) => handleFormChange('seo_slug', event.target.value)}
                placeholder="SEO URL slug, e.g. vijay-kr-yadav-choreographer-dhanbad"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
              />
              <input
                value={formData.meta_title || ''}
                onChange={(event) => handleFormChange('meta_title', event.target.value)}
                placeholder="Meta title, e.g. Santosh Kumar Sandilya | Painter from Varanasi | KalaManch"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
              />
              <textarea
                value={formData.meta_description || ''}
                onChange={(event) => handleFormChange('meta_description', event.target.value)}
                rows="3"
                placeholder="Meta description for search engines"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
              />
              <textarea
                value={formData.meta_keywords || ''}
                onChange={(event) => handleFormChange('meta_keywords', event.target.value)}
                rows="3"
                placeholder="Comma separated keywords, e.g. Santosh Kumar Sandilya, Varanasi painter, acrylic painter, Indian art, Kalamanch"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
              />
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={formData.image_alt_text || ''}
                  onChange={(event) => handleFormChange('image_alt_text', event.target.value)}
                  placeholder="Image ALT text, e.g. Santosh Kumar Sandilya painter from Varanasi"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                />
                <input
                  value={formData.image_title || ''}
                  onChange={(event) => handleFormChange('image_title', event.target.value)}
                  placeholder="Image title for search engines"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                />
              </div>
              <textarea
                value={formData.image_caption || ''}
                onChange={(event) => handleFormChange('image_caption', event.target.value)}
                rows="2"
                placeholder="Image caption for profile image, artwork previews, and search context"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
              />
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={formData.og_title || ''}
                  onChange={(event) => handleFormChange('og_title', event.target.value)}
                  placeholder="Open Graph title for WhatsApp, Facebook, and sharing cards"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                />
                <textarea
                  value={formData.og_description || ''}
                  onChange={(event) => handleFormChange('og_description', event.target.value)}
                  rows="2"
                  placeholder="Open Graph description for shared artist profile links"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                className="btn-outline"
                onClick={() => generateArtistBioSeo({ overwriteSeo: true })}
                disabled={aiSeoBusy || formBusy}
              >
                {aiSeoBusy ? 'Generating SEO...' : 'Regenerate SEO from Bio'}
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={handleSeoSave}
                disabled={seoBusy || formBusy}
              >
                {seoBusy ? 'Saving SEO...' : 'Save SEO Fields'}
              </button>
              {!editingArtistId && (
                <p className="text-xs font-semibold text-slate-600">
                  Save the artist profile first, then reopen it to store custom SEO values.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-sky-100 bg-sky-50/80 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-bold text-sky-950">Artist login credentials</p>
                <p className="mt-1 text-xs leading-5 text-sky-900">
                  Optional. Enter the artist's real email above and set a temporary password. The artist must verify email before login, then can change password from My Account.
                </p>
              </div>
              {editingArtistId && (
                <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-bold text-sky-800">
                  Leave blank to keep current password
                </span>
              )}
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
              <input
                type="password"
                value={formData.login_password || ''}
                onChange={(event) => handleFormChange('login_password', event.target.value)}
                placeholder={editingArtistId ? 'New temporary password (optional)' : 'Temporary password for artist login'}
                className="w-full rounded-2xl border border-sky-200 bg-white px-4 py-3"
                autoComplete="new-password"
              />
              <label className="inline-flex items-center gap-3 rounded-2xl border border-sky-200 bg-white px-4 py-3 text-sm font-bold text-sky-950">
                <input
                  type="checkbox"
                  checked={Boolean(formData.send_login_email)}
                  onChange={(event) => handleFormChange('send_login_email', event.target.checked)}
                />
                Email login details
              </label>
            </div>
            <p className="mt-2 text-xs text-sky-900">
              Password must follow the site password rules. The backend stores only a secure hash, not the plain password.
            </p>
          </div>

            <div className="flex flex-wrap gap-6">
              <label className="inline-flex items-center gap-3 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={formData.is_verified} onChange={(event) => handleFormChange('is_verified', event.target.checked)} />
                Verified
              </label>
              <label className="inline-flex items-center gap-3 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={formData.is_featured} onChange={(event) => handleFormChange('is_featured', event.target.checked)} />
                Featured
              </label>
              <label className="inline-flex items-center gap-3 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={formData.is_spotlight} onChange={(event) => handleFormChange('is_spotlight', event.target.checked)} />
                Spotlight
              </label>
            </div>

            <div className="rounded-3xl border border-purple-100 bg-purple-50/60 p-4">
              <label className="text-sm font-black text-purple-950">Public trust badge override</label>
              <p className="mt-1 text-xs font-semibold text-purple-800">
                Controls the public trust badge independently from admin-only verification and featuring.
              </p>
              <select
                value={formData.trust_badge_override || 'none'}
                onChange={(event) => handleFormChange('trust_badge_override', event.target.value)}
                className="mt-3 w-full rounded-2xl border border-purple-200 bg-white px-4 py-3"
              >
                {TRUST_BADGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {draftStore.error && <p role="alert" className="text-sm text-red-700">{draftStore.error}</p>}
              {formError && <p className="text-sm text-red-600">{formError}</p>}
            {formNotice && <p className="text-sm text-green-700">{formNotice}</p>}

            <div className="flex flex-wrap gap-3">
              <button type="submit" className="btn-primary" disabled={formBusy || imageUploading}>
                {formBusy ? 'Saving...' : editingArtistId ? 'Save Artist Changes' : 'Create Artist'}
              </button>
              <button type="button" className="btn-outline" onClick={saveCurrentDraft} disabled={formBusy || imageUploading}>Save Draft</button>
              <button type="button" className="btn-outline" onClick={resetForm} disabled={formBusy || imageUploading}>Cancel</button>
            </div>
          </form>
          </section>
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
        <button
          type="button"
          onClick={() => {
            setStatusFilter('all');
            setCategoryFilter('all');
            setSearch('');
          }}
          className="bg-white rounded-[1.75rem] border border-orange-100 shadow-sm p-5 text-left transition hover:bg-orange-50"
        >
          <p className="text-sm text-gray-500">Total artists</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{summary.total}</p>
          <p className="mt-1 text-xs font-semibold text-[#FF6F00]">Show all artists</p>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter('verified')}
          className="bg-white rounded-[1.75rem] border border-green-100 shadow-sm p-5 text-left transition hover:bg-green-50"
        >
          <p className="text-sm text-gray-500">Verified</p>
          <p className="text-3xl font-bold text-green-700 mt-2">{summary.verified}</p>
          <p className="mt-1 text-xs font-semibold text-green-700">Filter verified</p>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter('featured')}
          className="bg-white rounded-[1.75rem] border border-orange-100 shadow-sm p-5 text-left transition hover:bg-orange-50"
        >
          <p className="text-sm text-gray-500">Featured</p>
          <p className="text-3xl font-bold text-[#FF6F00] mt-2">{summary.featured}</p>
          <p className="mt-1 text-xs font-semibold text-[#FF6F00]">Filter featured</p>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter('spotlight')}
          className="bg-white rounded-[1.75rem] border border-purple-100 shadow-sm p-5 text-left transition hover:bg-purple-50"
        >
          <p className="text-sm text-gray-500">Spotlight</p>
          <p className="text-3xl font-bold text-purple-700 mt-2">{summary.spotlight}</p>
          <p className="mt-1 text-xs font-semibold text-purple-700">Filter spotlight</p>
        </button>
        <div className="bg-white rounded-[1.75rem] border border-teal-100 shadow-sm p-5">
          <p className="text-sm text-gray-500">With Portfolio</p>
          <p className="text-3xl font-bold text-[#0F766E] mt-2">{summary.withPortfolio}</p>
        </div>
        <button
          type="button"
          onClick={() => setStatusFilter('native_pending')}
          className="bg-white rounded-[1.75rem] border border-emerald-100 shadow-sm p-5 text-left transition hover:bg-emerald-50"
        >
          <p className="text-sm text-gray-500">Native Review</p>
          <p className="text-3xl font-bold text-emerald-700 mt-2">{summary.nativePending}</p>
          <p className="mt-1 text-xs font-semibold text-emerald-700">Pending approval</p>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter('push_on')}
          className="bg-white rounded-[1.75rem] border border-sky-100 shadow-sm p-5 text-left transition hover:bg-sky-50"
        >
          <p className="text-sm text-gray-500">Push Opted-In</p>
          <p className="text-3xl font-bold text-sky-700 mt-2">{summary.pushOptedIn}</p>
          <p className="mt-1 text-xs font-semibold text-sky-700">{summary.pushActiveDevices} active app device(s)</p>
        </button>
        <div className="bg-white rounded-[1.75rem] border border-slate-100 shadow-sm p-5 sm:col-span-2 xl:col-span-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-500">Average Profile Strength</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.averageScore}<span className="text-base text-gray-500">/100</span></p>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100 sm:max-w-xl">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${profileScoreBarClass(summary.averageScore >= 85 ? 'green' : summary.averageScore >= 70 ? 'teal' : summary.averageScore >= 50 ? 'orange' : 'red')}`}
                style={{ width: `${summary.averageScore}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg border border-orange-100 p-5 sm:p-6">
        {(statusFilter !== 'all' || categoryFilter !== 'all' || search.trim()) && (
          <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm font-semibold text-gray-700">
              Showing <span className="font-black text-[#FF6F00]">{filteredArtists.length}</span> of {summary.total} artists
              {statusFilter !== 'all' && (
                <span className="ml-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-[#FF6F00]">
                  {adminArtistStatusLabels[statusFilter] || statusFilter}
                </span>
              )}
              {categoryFilter !== 'all' && (
                <span className="ml-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-[#4A148C]">
                  {categoryFilter}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setStatusFilter('all');
                setCategoryFilter('all');
                setSearch('');
              }}
              className="rounded-full bg-white px-4 py-2 text-sm font-bold text-[#FF6F00] shadow-sm transition hover:bg-orange-100"
            >
              Show all artists
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, email, mobile, or category"
            className="w-full rounded-2xl border border-gray-300 px-4 py-3"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white"
          >
            <option value="all">All statuses</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
            <option value="pending_email">Pending email verification</option>
            <option value="review_ready">Ready for review</option>
            <option value="incomplete_signup">Incomplete signup</option>
            <option value="featured">Featured</option>
            <option value="spotlight">Spotlight</option>
            <option value="native_pending">Native bio pending review</option>
            <option value="native_verified">Native language verified</option>
            <option value="native_added">Native language added</option>
            <option value="push_on">Push notifications opted-in</option>
            <option value="push_off">Push notifications off/no app device</option>
            <option value="standard">Not featured</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white"
          >
            <option value="all">All categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg border border-orange-100 overflow-hidden">
        {messageNotice && <div className="m-5 rounded-2xl bg-green-50 border border-green-200 px-4 py-3 text-green-700 text-sm">{messageNotice}</div>}
        {trustBadgeNotice && <div className="m-5 rounded-2xl bg-green-50 border border-green-200 px-4 py-3 text-green-700 text-sm">{trustBadgeNotice}</div>}
        {trustBadgeError && <div className="m-5 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm">{trustBadgeError}</div>}
        {loading ? (
          <div className="p-6 text-gray-500">Loading artists...</div>
        ) : filteredArtists.length === 0 ? (
          <div className="p-6 text-gray-500">No artists match the current filters.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {filteredArtists.map((artist) => {
              const isBusy = busyArtistId === artist.id;
              const profileAnalysis = analyzeArtistProfile(artist);
              const reviewChecklist = getArtistReviewChecklist(artist);
              const reviewReadyCount = reviewChecklist.filter((item) => item.complete).length;
              const emailVerified = hasVerifiedEmail(artist);
              const isReadyForReview = emailVerified && profileAnalysis.score >= 70;
              const socialProfiles = getArtistSocialProfiles(artist);
              const aiPanel = aiPanelByArtist[artist.id];
              const aiPanelLoading = Boolean(aiPanelLoadingByArtist[artist.id]);
              const aiPanelError = aiPanelErrorByArtist[artist.id];

              return (
                <div
                  id={`admin-artist-${artist.id}`}
                  key={artist.id}
                  className={`p-5 sm:p-6 space-y-5 transition ${
                    focusArtistId === artist.id ? 'bg-orange-50/80 ring-2 ring-[#FF6F00]/40' : ''
                  }`}
                >
                  <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-2xl font-semibold text-gray-900">{artist.name}</h3>
                        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${profileSourceBadgeClass(artist)}`}>
                          {profileSourceLabel(artist)}
                        </span>
                        <span className="bg-orange-100 text-[#FF6F00] px-3 py-1 rounded-full text-xs font-semibold">
                          {artist.category || 'Uncategorized'}
                        </span>
                        {artist.is_verified && (
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Verified
                          </span>
                        )}
                        {!emailVerified && (
                          <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold">
                            Pending email verification
                          </span>
                        )}
                        {artist.name_needs_correction && (
                          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Real name required
                          </span>
                        )}
                        {artist.is_featured && (
                          <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Featured
                          </span>
                        )}
                        {artist.is_spotlight && (
                          <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Spotlight
                          </span>
                        )}
                        {artist.trust_badge_override && artist.trust_badge_override !== 'none' && (
                          <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold">
                            {trustBadgeLabel(artist.trust_badge_override)}
                          </span>
                        )}
                        {hasNativeReviewPending(artist) && (
                          <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold">
                            Native review needed
                          </span>
                        )}
                        {artist.native_language_verified && (
                          <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold">
                            {artist.native_language_badge || `Native: ${artist.native_language || 'verified'}`}
                          </span>
                        )}
                        {artist.privacy_policy_accepted && artist.terms_accepted && artist.trust_safety_accepted && (
                          <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Policies accepted
                          </span>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${hasPushOptIn(artist) ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'}`}>
                          {hasPushOptIn(artist) ? `Push ON (${artist.push_notification_device_count || 1})` : 'Push OFF'}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>{artist.email || 'No email added'}</p>
                        <p>{artist.mobile || 'No mobile added'}</p>
                        <p>{artist.display_profession || artist.subcategory || 'No public profession label'}</p>
                        {!emailVerified && artist.verification_email_last_attempt_at && (
                          <p className={artist.verification_email_last_sent ? 'font-semibold text-emerald-700' : 'font-semibold text-red-700'}>
                            Last verification email: {artist.verification_email_last_sent ? 'sent' : 'delivery failed'} on {new Date(artist.verification_email_last_attempt_at).toLocaleString('en-IN')}
                          </p>
                        )}
                        {artist.privacy_policy_accepted && artist.terms_accepted && artist.trust_safety_accepted && (
                          <p className="font-semibold text-sky-700">
                            Policies accepted{artist.policy_accepted_at ? ` on ${new Date(artist.policy_accepted_at).toLocaleDateString('en-IN')}` : ''}
                          </p>
                        )}
                      </div>
                      <div className="max-w-4xl rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Social profiles</p>
                          <p className="text-xs font-semibold text-slate-500">
                            {socialProfiles.length ? `${socialProfiles.length} profile${socialProfiles.length > 1 ? 's' : ''} added` : 'No social profile added'}
                          </p>
                        </div>
                        {socialProfiles.length ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {socialProfiles.map((profile) => (
                              <a
                                key={profile.key}
                                href={profile.url}
                                target="_blank"
                                rel="noreferrer"
                                className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold transition hover:-translate-y-0.5 hover:shadow-sm ${profile.tone}`}
                                title={profile.url}
                              >
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/80 text-[10px]">
                                  {profile.shortLabel}
                                </span>
                                <span>{profile.label}</span>
                                <span className="max-w-[180px] truncate font-semibold opacity-80">
                                  {socialProfileDisplayName(profile.url)}
                                </span>
                                {profile.followerLabel && (
                                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide opacity-80">
                                    {profile.followerLabel}
                                  </span>
                                )}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">Instagram, Facebook, YouTube, or Spotify links have not been added yet.</p>
                        )}
                      </div>
                      {artist.bio && <p className="text-sm text-gray-600 max-w-4xl">{artist.bio}</p>}
                      {hasNativeProfile(artist) && (
                        <div className="max-w-4xl rounded-2xl border border-emerald-100 bg-emerald-50/70 p-3 text-sm">
                          <p className="font-bold text-emerald-900">
                            Native profile: {artist.native_language || 'Language not set'}
                          </p>
                          {artist.native_bio && (
                            <p className="mt-1 line-clamp-2 text-emerald-900" dir="auto">{artist.native_bio}</p>
                          )}
                          {!artist.native_language_verified && artist.native_bio && (
                            <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                              <p className="font-black">Review needed before public badge appears.</p>
                              <p className="mt-1">Suggested badge: {artist.native_language_badge || suggestedNativeBadge(artist)}</p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isReadyForReview ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-[#FF6F00]'}`}>
                          {isReadyForReview ? 'Ready for review' : emailVerified ? 'Incomplete signup' : 'Pending email verification'}
                        </span>
                        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${profileScoreBadgeClass(profileAnalysis.tone)}`}>
                          Profile Strength {profileAnalysis.score}/100
                        </span>
                        <span className="text-xs text-gray-500">
                          {reviewReadyCount}/{reviewChecklist.length} review items complete
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <select
                        value={artist.trust_badge_override || 'none'}
                        onChange={(event) => handleTrustBadgeOverride(artist.id, event.target.value)}
                        disabled={isBusy}
                        className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700"
                        aria-label={`Trust badge for ${artist.name}`}
                      >
                        {TRUST_BADGE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <ArtistActionButton
                        className={`border ${artist.is_verified ? 'border-green-200 bg-green-50 text-green-700' : 'border-orange-200 bg-orange-50 text-[#FF6F00]'}`}
                        onClick={() => handleVerify(artist.id)}
                        disabled={isBusy || artist.is_verified || !emailVerified || artist.name_needs_correction}
                        title={!emailVerified ? 'Email verification is required first.' : artist.name_needs_correction ? "Add the artist's real name first." : ''}
                      >
                        {artist.is_verified ? 'Verified' : 'Verify Artist'}
                      </ArtistActionButton>
                      {!emailVerified && artist.email && (
                        <ArtistActionButton
                          className="border border-amber-200 bg-amber-50 text-amber-800"
                          onClick={() => handleResendEmailVerification(artist)}
                          disabled={isBusy}
                        >
                          Resend Verification Email
                        </ArtistActionButton>
                      )}
                      <ArtistActionButton
                        className={`border ${artist.is_featured ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-orange-200 bg-orange-50 text-[#FF6F00]'}`}
                        onClick={() => (artist.is_featured ? handleUnfeature(artist.id) : handleFeature(artist.id))}
                        disabled={isBusy}
                      >
                        {artist.is_featured ? 'Remove Featured' : 'Feature Artist'}
                      </ArtistActionButton>
                      <ArtistActionButton
                        className={`border ${artist.is_spotlight ? 'border-purple-200 bg-purple-700 text-white' : 'border-purple-200 bg-purple-50 text-purple-700'}`}
                        onClick={() => handleSpotlight(artist.id, artist.is_spotlight)}
                        disabled={isBusy || (!artist.is_verified && !artist.is_spotlight)}
                      >
                        {artist.is_spotlight ? 'Remove Spotlight' : 'Set Spotlight'}
                      </ArtistActionButton>
                      <ArtistActionButton
                        className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        onClick={() => handleEdit(artist)}
                        disabled={isBusy}
                      >
                        Edit Artist
                      </ArtistActionButton>
                      {hasNativeReviewPending(artist) && (
                        <ArtistActionButton
                          className="border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          onClick={() => handleEdit(artist, 'Review the native-language bio, add badge text if needed, then tick native language verification and save.')}
                          disabled={isBusy}
                        >
                          Review Native Bio
                        </ArtistActionButton>
                      )}
                      <ArtistActionButton
                        className="border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100"
                        onClick={() => {
                          setMessageArtistId((current) => (current === artist.id ? '' : artist.id));
                          setArtistMessage('');
                          setMessageNotice('');
                        }}
                        disabled={isBusy}
                      >
                        Message Artist
                      </ArtistActionButton>
                      {[['3 months', '3 months'], ['6 months', '6 months'], ['1 year', '1 year']].map(([key, label]) => (
                        <ArtistActionButton
                          key={`${artist.id}-${key}`}
                          className="border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          onClick={() => {
                            const milestone = buildMilestoneMessage(artist, label);
                            setMessageArtistId(artist.id);
                            setArtistMessage(milestone.message);
                            setMessageNotice('');
                          }}
                          disabled={isBusy}
                        >
                          {label}
                        </ArtistActionButton>
                      ))}
                      <a
                          href={artistProfilePath(artist)}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full px-4 py-2 text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
                      >
                        View Profile
                      </a>
                      <ArtistActionButton
                        className="border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                        onClick={() => handleDelete(artist.id)}
                        disabled={isBusy}
                      >
                        Delete
                      </ArtistActionButton>
                    </div>
                  </div>

                  {messageArtistId === artist.id && (
                    <div className="rounded-3xl border border-purple-100 bg-purple-50/60 p-4 space-y-3">
                      <p className="text-sm font-semibold text-purple-800">Send profile review note to {artist.name}</p>
                      <textarea
                        value={artistMessage}
                        onChange={(event) => setArtistMessage(event.target.value)}
                        rows="3"
                        placeholder="Example: Please add a clearer profile photo and one YouTube or Instagram portfolio link before verification."
                        className="w-full rounded-2xl border border-purple-200 px-4 py-3"
                      />
                      <div className="flex flex-wrap gap-3">
                        <ArtistActionButton
                          className="border border-purple-200 bg-purple-700 text-white hover:bg-purple-800"
                          onClick={() => handleSendAdminMessage(artist.id)}
                          disabled={isBusy || !artistMessage.trim()}
                        >
                          Send Message
                        </ArtistActionButton>
                        <ArtistActionButton
                          className="border border-slate-200 bg-white text-slate-700"
                          onClick={() => {
                            setMessageArtistId('');
                            setArtistMessage('');
                          }}
                          disabled={isBusy}
                        >
                          Cancel
                        </ArtistActionButton>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Portfolio</p>
                      <p className="font-semibold text-gray-800 mt-1">{socialProfiles.length ? `${socialProfiles.length} social link${socialProfiles.length > 1 ? 's' : ''}` : 'Missing'}</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Image</p>
                      <p className="font-semibold text-gray-800 mt-1">{artist.image_url ? 'Uploaded' : 'Missing'}</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Created</p>
                      <p className="font-semibold text-gray-800 mt-1">
                        {formatCreatedAt(artist.created_at)}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {getDaysOnKalamanch(artist.created_at) === null ? 'Days on KalaManch unavailable' : `${getDaysOnKalamanch(artist.created_at)} day(s) on KalaManch`}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Artist ID</p>
                      <p className="font-semibold text-gray-800 mt-1 break-all">{artist.id}</p>
                    </div>
                    <div className="rounded-2xl bg-sky-50 px-4 py-3 md:col-span-4">
                      <p className="text-sky-700">Push notifications</p>
                      <p className="font-semibold text-gray-800 mt-1">
                        {hasPushOptIn(artist)
                          ? `Opted in on ${(artist.push_notification_platforms || ['android']).join(', ')}`
                          : 'No active app device or notifications turned off'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">{formatPushSync(artist.push_notification_last_sync_at)}</p>
                    </div>
                  </div>
                  <div className="rounded-3xl border border-slate-100 bg-slate-50/70 p-4">
                    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">AI Profile Coach</p>
                        <p className="mt-1 text-xs text-gray-500">Use this score before verifying, featuring, or spotlighting.</p>
                      </div>
                      <div className="h-2.5 w-full overflow-hidden rounded-full bg-white sm:max-w-xs">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${profileScoreBarClass(profileAnalysis.tone)}`}
                          style={{ width: `${profileAnalysis.score}%` }}
                        />
                      </div>
                    </div>
                    {profileAnalysis.suggestions.length > 0 && (
                      <div className="mb-3 grid gap-2 sm:grid-cols-2">
                        {profileAnalysis.suggestions.map((suggestion) => (
                          <p key={suggestion} className="rounded-2xl bg-white px-3 py-2 text-xs text-gray-700">
                            {suggestion}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      {reviewChecklist.map((item) => (
                        <div
                          key={item.label}
                          className={`rounded-2xl border px-3 py-2 text-xs font-semibold ${
                            item.complete || item.partial
                              ? 'border-green-100 bg-green-50 text-green-700'
                              : 'border-orange-100 bg-orange-50 text-orange-700'
                          }`}
                        >
                          <span>{item.label}</span>
                          <span className="ml-2">{item.complete ? 'Done' : item.partial ? 'Improve' : 'Needed'}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-indigo-100 bg-indigo-50/40 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-indigo-900">Artist AI panel</p>
                        <p className="mt-1 text-xs text-indigo-700/80">
                          Summary, trust score, moderation notes, and latest monthly growth snapshot for admin review.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => loadArtistAiPanel(artist.id, { force: true })}
                        disabled={aiPanelLoading}
                        className="rounded-full border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {aiPanelLoading ? 'Loading AI panel...' : aiPanel ? 'Refresh AI panel' : 'Load AI panel'}
                      </button>
                    </div>

                    {aiPanelError && (
                      <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {aiPanelError}
                      </div>
                    )}

                    {!aiPanel && !aiPanelLoading && !aiPanelError && (
                      <div className="mt-4 rounded-2xl border border-dashed border-indigo-200 bg-white/80 px-4 py-5 text-sm text-indigo-800">
                        Load this panel when you want the latest AI summary, trust issues, moderation history, and monthly growth notes for {artist.name}.
                      </div>
                    )}

                    {aiPanel && (
                      <div className="mt-4 grid gap-4 xl:grid-cols-2">
                        <div className="rounded-2xl border border-white bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-700">AI Summary</p>
                              <p className="mt-1 text-xs text-gray-500">
                                Generated {formatDateTime(aiPanel.summary?.generated_at)}
                              </p>
                            </div>
                            <span className={`rounded-full px-3 py-1 text-xs font-bold ${aiPanel.summary?.available ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {aiPanel.summary?.available ? 'Available' : 'Not generated'}
                            </span>
                          </div>
                          <p className="mt-3 text-lg font-black text-gray-950">
                            {aiPanel.summary?.tagline || 'No AI tagline yet'}
                          </p>
                          <p className="mt-3 text-sm leading-6 text-gray-700">
                            {aiPanel.summary?.hero_bio_english || 'No English AI summary generated yet.'}
                          </p>
                          {aiPanel.summary?.hero_bio_hinglish && (
                            <p className="mt-3 rounded-2xl bg-indigo-50 px-3 py-3 text-sm leading-6 text-indigo-900">
                              {aiPanel.summary.hero_bio_hinglish}
                            </p>
                          )}
                          {Array.isArray(aiPanel.summary?.strengths) && aiPanel.summary.strengths.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {aiPanel.summary.strengths.map((strength) => (
                                <span key={strength} className="rounded-full border border-indigo-100 bg-white px-3 py-1 text-xs font-semibold text-indigo-700">
                                  {strength}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="rounded-2xl border border-white bg-white p-4">
                          <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-700">Trust Score</p>
                          <div className="mt-3 flex items-end gap-3">
                            <p className="text-4xl font-black text-gray-950">{aiPanel.trust?.score ?? 0}</p>
                            <p className="pb-1 text-sm font-semibold text-gray-500">/ 100</p>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                              Signup {aiPanel.trust?.signup_status || 'not_started'}
                            </span>
                            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-bold text-indigo-700">
                              {trustBadgeLabel(aiPanel.trust?.badge_override || 'none') || 'No public trust badge'}
                            </span>
                          </div>
                          {Array.isArray(aiPanel.trust?.issues) && aiPanel.trust.issues.length > 0 ? (
                            <div className="mt-4 space-y-2">
                              {aiPanel.trust.issues.map((issue) => (
                                <p key={issue} className="rounded-2xl bg-orange-50 px-3 py-2 text-sm text-orange-800">
                                  {issue}
                                </p>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-4 rounded-2xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                              No trust warnings right now.
                            </p>
                          )}
                        </div>

                        <div className="rounded-2xl border border-white bg-white p-4">
                          <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-700">Moderation Notes</p>
                          {Array.isArray(aiPanel.moderation?.logs) && aiPanel.moderation.logs.length > 0 ? (
                            <div className="mt-3 space-y-3">
                              {aiPanel.moderation.logs.map((log, index) => (
                                <div key={`${log.content_id || 'content'}-${log.checked_at || index}`} className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="rounded-full bg-white px-2 py-1 text-[11px] font-black text-slate-700">
                                      {log.action || 'HUMAN_REVIEW'}
                                    </span>
                                    <span className="text-xs font-semibold text-gray-500">
                                      {formatDateTime(log.checked_at)}
                                    </span>
                                  </div>
                                  <p className="mt-2 text-sm font-semibold text-gray-800">
                                    {log.verdict || 'No moderation verdict saved.'}
                                  </p>
                                  {log.moderator_note && (
                                    <p className="mt-2 text-sm leading-6 text-gray-600">{log.moderator_note}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-3 rounded-2xl bg-slate-50 px-3 py-3 text-sm text-slate-600">
                              No moderation notes found for this artist yet.
                            </p>
                          )}
                        </div>

                        <div className="rounded-2xl border border-white bg-white p-4">
                          <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-700">Monthly Growth Snapshot</p>
                          {aiPanel.monthly_growth?.month_label ? (
                            <>
                              <p className="mt-2 text-sm font-semibold text-gray-500">
                                {aiPanel.monthly_growth.month_label} • Generated {formatDateTime(aiPanel.monthly_growth.generated_at)}
                              </p>
                              <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="rounded-2xl bg-slate-50 px-3 py-3">
                                  <p className="text-xs font-semibold text-gray-500">Posts</p>
                                  <p className="mt-1 text-2xl font-black text-gray-950">{aiPanel.monthly_growth.posts_count || 0}</p>
                                </div>
                                <div className="rounded-2xl bg-slate-50 px-3 py-3">
                                  <p className="text-xs font-semibold text-gray-500">Views</p>
                                  <p className="mt-1 text-2xl font-black text-gray-950">{aiPanel.monthly_growth.views || 0}</p>
                                </div>
                                <div className="rounded-2xl bg-slate-50 px-3 py-3">
                                  <p className="text-xs font-semibold text-gray-500">Profile visits</p>
                                  <p className="mt-1 text-2xl font-black text-gray-950">{aiPanel.monthly_growth.profile_visits || 0}</p>
                                </div>
                                <div className="rounded-2xl bg-slate-50 px-3 py-3">
                                  <p className="text-xs font-semibold text-gray-500">Gig inquiries</p>
                                  <p className="mt-1 text-2xl font-black text-gray-950">{aiPanel.monthly_growth.gig_inquiries || 0}</p>
                                </div>
                              </div>
                              {(aiPanel.monthly_growth.top_artwork_title || aiPanel.monthly_growth.report_text) && (
                                <div className="mt-4 rounded-2xl bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                                  {aiPanel.monthly_growth.top_artwork_title && (
                                    <p className="font-bold">
                                      Best moment: {aiPanel.monthly_growth.top_artwork_title}
                                      {aiPanel.monthly_growth.top_artwork_views ? ` (${aiPanel.monthly_growth.top_artwork_views} views)` : ''}
                                    </p>
                                  )}
                                  {aiPanel.monthly_growth.report_text && (
                                    <p className="mt-2 whitespace-pre-line leading-6">{aiPanel.monthly_growth.report_text}</p>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="mt-3 rounded-2xl bg-slate-50 px-3 py-3 text-sm text-slate-600">
                              No monthly AI growth snapshot has been generated for this artist yet.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminArtists;
